// ─── SCHEDULE SNAPSHOT ─────────────────────────────────────────────────────────
// Standalone "what does the schedule actually look like right now" builder —
// fetches calendar events + pickups for a family across a date window and
// groups them into the { isoDate, events, pickups }[] shape schedulePublish.js
// expects. Independent of any screen's component state (unlike Review &
// Assign's own fetchData, which layers on rules/alerts/session UI concerns),
// so it can run from anywhere — a batched auto-publish flush triggered by a
// "Running late" unassign, a recurring-series edit, or a catch-up check on
// app load, none of which have a Review & Assign screen mounted.
//
// Reuses eventDates() (lib/datetime.js) for multi-day event expansion — the
// same helper Review & Assign's own day-grouping uses, so a fix there fixes
// both instead of the two silently drifting apart.

import { fetchEvents as gcalFetchEvents } from '../lib/gcal';
import { fsGetCol } from '../lib/firebase';
import { pickupsForDate, overrideKey } from '../lib/pickups';
import { eventDates, formatEventTime } from '../lib/datetime';

const WINDOW_DAYS = 14; // matches ScheduleTab's caregiver-facing horizon

export async function fetchScheduleWindow(familyData, { windowDays = WINDOW_DAYS } = {}) {
  if (!familyData?.id) return [];

  const calAssignments = familyData?.config?.calendarAssignments || {};
  const suppressedCals = familyData?.config?.suppressedCalendars || [];
  let calendarIds = Object.keys(calAssignments).filter(id => !suppressedCals.includes(id));
  if (calendarIds.length === 0) calendarIds = ['primary'];

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const horizon = new Date(today);
  horizon.setDate(horizon.getDate() + windowDays);
  horizon.setHours(23, 59, 59, 999);

  const rawEvents = [];
  for (const calId of calendarIds) {
    try {
      const evts = await gcalFetchEvents({
        calendarId: calId, timeMin: today.toISOString(), timeMax: horizon.toISOString(), maxResults: 250,
      });
      for (const e of (evts || [])) rawEvents.push({ ...e, _calendarId: calId });
    } catch (err) {
      console.warn('[scheduleSnapshot] calendar fetch failed:', calId, err.message);
    }
  }
  const seenIds = new Set();
  const dedupedEvents = rawEvents.filter(e => { if (seenIds.has(e.id)) return false; seenIds.add(e.id); return true; });

  let ignoredMap = {};
  try {
    const ignored = await fsGetCol(`families/${familyData.id}/ignoredEvents`);
    for (const i of (ignored || [])) ignoredMap[i.id] = i;
  } catch (err) {
    console.warn('[scheduleSnapshot] ignored events fetch failed:', err.message);
  }

  const startDateStr = today.toISOString().split('T')[0];
  const endDateStr = horizon.toISOString().split('T')[0];

  // Group into per-day buckets, expanding multi-day (all-day) events across
  // every day they cover, clipped to the fetched window.
  const eventsByDate = {};
  for (const evt of dedupedEvents) {
    if (ignoredMap[evt.id] || (evt.recurringEventId && ignoredMap[evt.recurringEventId])) continue;
    const dates = eventDates(evt).filter(d => d >= startDateStr && d <= endDateStr);
    if (!dates.length) continue;
    const row = { summary: evt.summary || '(No title)', startTime: formatEventTime(evt), description: evt.description, _raw: evt };
    for (const date of dates) {
      if (!eventsByDate[date]) eventsByDate[date] = [];
      eventsByDate[date].push(row);
    }
  }

  let overridesMap = {};
  try {
    const overrides = await fsGetCol(`families/${familyData.id}/pickupOverrides`, [
      { field: 'date', op: '>=', value: startDateStr },
    ]);
    for (const o of (overrides || [])) {
      if (o.date > endDateStr) continue;
      overridesMap[overrideKey(o.childId, o.date, o.routineId, o.leg)] = o;
    }
  } catch (err) {
    console.warn('[scheduleSnapshot] pickup overrides fetch failed:', err.message);
  }

  const pickupsActive = familyData?.config?.pickupsActive !== false;
  const days = [];
  const cursor = new Date(today);
  while (cursor <= horizon) {
    const isoDate = cursor.toISOString().split('T')[0];
    days.push({
      isoDate,
      events: eventsByDate[isoDate] || [],
      pickups: pickupsForDate(familyData?.children || [], isoDate, overridesMap, { active: pickupsActive }),
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}
